import { useFormik } from 'formik';
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { object, ref, string } from 'yup';
import theRapixLOgo from "../assets/Logo_main.svg";
import theRapixLOgoSmall from '../assets/Logo_small.svg';
import therapixDarkLogo from '../assets/therapix-03.svg';

import FieldWrap from '../components/form/FieldWrap';
import Input from '../components/form/Input';
import Select from '../components/form/Select';
import { TSelectOptions } from '../components/form/SelectReact';
import Validation from '../components/form/Validation';
import Icon from '../components/icon/Icon';
import PageWrapper from '../components/layouts/PageWrapper/PageWrapper';
import Button from '../components/ui/Button';
import { authPages } from '../config/pages.config';
import { countries, statesJson } from '../constants/common/other.constant';
import { useAuth } from '../context/authContext';
import { signUpUser } from '../services/apiService';
import LogoTemplate from '../templates/layouts/Logo/Logo.template';

export type SignupTValues = {
	firstName: string;
	lastName: string;
	email: string;
	mobile: string;
	password: string;
	confirmpassword?: string;
	country: string;
	state: string;
};

const validationSchema = object({
	firstName: string().required('Required'),
	lastName: string().required('Required'),
	country: string().required('Required'),
	state: string().required('Required'),
	mobile: string()
		.required('Required')
		.matches(
			/^(\+\d{1,3}[- ]?)?\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/, 
			'Please enter a valid phone number. Examples: +1-234-567-8901, (123) 456-7890, or 1234567890'
		),
	email: string()
	  .email('Invalid email address')
	  .required('Required'),
	password: string()
	  .min(6, 'Password must be at least 6 characters')
	  .matches( /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[A-Za-z\d\W_]+$/,
		'Password must contain at least one uppercase letter, one number, and one special character'
	  )
	  .required('Required'),
	confirmpassword: string()
		.oneOf([ref('password')], 'Passwords must match')
		.required('Required'),
});

const SignupPage = () => {
	const navigate = useNavigate();
	const { call } = signUpUser();
	const { onLogout } = useAuth();
	const [passwordShowStatus, setPasswordShowStatus] = useState<boolean>(false);
	const [confirmPasswordShowStatus, setConfirmPasswordShowStatus] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const gradientRef = useRef<HTMLDivElement>(null);
	const isHoveringRef = useRef(false);
	const animationFrameRef = useRef<number | null>(null);

	useEffect(() => {
		let targetX = 50;
		let targetY = 50;
		let currentX = 50;
		let currentY = 50;
		let lastUpdateTime = 0;
		let velocityX = 0.01;
		let velocityY = 0.015;
		
		const updateGradientPosition = (timestamp: number) => {
			if (!gradientRef.current) return;
			
			const delta = timestamp - lastUpdateTime;
			lastUpdateTime = timestamp;
			
			// Auto movement when not hovering
			if (!isHoveringRef.current) {
				// Continuous movement with smooth bounce
				currentX += velocityX * delta * 0.5;
				currentY += velocityY * delta * 0.5;
				
				// Bounce when reaching edges
				if (currentX <= 25 || currentX >= 75) {
					velocityX *= -1;
				}
				if (currentY <= 25 || currentY >= 75) {
					velocityY *= -1;
				}
				
				// Ensure within boundaries
				currentX = Math.max(25, Math.min(75, currentX));
				currentY = Math.max(25, Math.min(75, currentY));
			}
			
			// Apply the gradient with current position and smaller radius
			gradientRef.current.style.background = 
				`radial-gradient(circle at ${currentX}% ${currentY}%, #557C93 0%, #08203E 55%)`;
			
			animationFrameRef.current = requestAnimationFrame(updateGradientPosition);
		};
		
		const handleMouseMove = (e: MouseEvent) => {
			if (!gradientRef.current) return;
			
			const rect = gradientRef.current.getBoundingClientRect();
			const x = ((e.clientX - rect.left) / rect.width) * 100;
			const y = ((e.clientY - rect.top) / rect.height) * 100;
			
			// Update current position directly when mouse is moving
			currentX = x;
			currentY = y;
		};
		
		const handleMouseEnter = () => {
			isHoveringRef.current = true;
		};
		
		const handleMouseLeave = () => {
			isHoveringRef.current = false;
		};
		
		const gradient = gradientRef.current;
		if (gradient) {
			gradient.addEventListener('mousemove', handleMouseMove);
			gradient.addEventListener('mouseenter', handleMouseEnter);
			gradient.addEventListener('mouseleave', handleMouseLeave);
			
			// Start animation loop
			animationFrameRef.current = requestAnimationFrame(updateGradientPosition);
		}
		
		return () => {
			if (gradient) {
				gradient.removeEventListener('mousemove', handleMouseMove);
				gradient.removeEventListener('mouseenter', handleMouseEnter);
				gradient.removeEventListener('mouseleave', handleMouseLeave);
			}
			
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, []);

	// Independent form state, not using Formik's submission handling
	const formInitialValues = {
		firstName: '',
		lastName: '',
		email: '',
		mobile: '',
		password: '',
		confirmpassword: '',
		country: '',
		state: '',
	};

	const formik = useFormik({
		initialValues: formInitialValues,
		validationSchema,
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: () => {}, // Empty onSubmit as we handle it manually
	});

	const [statesOptions, setStatesOptions] = useState<TSelectOptions>(
		statesJson[formik.values.country] ?? []
	);

	useEffect(() => {
		formik.setFieldValue('state', '');
		setStatesOptions(statesJson[formik.values.country] ?? []);
	}, [formik.values.country]);

	const handleCheckboxChange = () => {
		setIsChecked(!isChecked);
	};

	const handleFormSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		try {
			// Validate all fields first and show errors
			const errors = await formik.validateForm();
			
			// Touch all fields to show validation messages
			Object.keys(formik.values).forEach(key => {
				formik.setFieldTouched(key, true);
			});
			
			// Check if there are any errors
			if (Object.keys(errors).length > 0) {
				return;
			}
			
			if (isSubmitting) return;
			
			// Set submitting state
			setIsSubmitting(true);
			
			// Create a copy of the form values to avoid reference issues
			const formData = {...formik.values};
			
			// Manual API call instead of using Observable
			try {
				const subscription = call(formData);
				
				subscription.subscribe({
					next: (res: any) => {
						toast.success(res.msg);
						navigate(authPages.loginPage.to);
					},
					error: (e: any) => {
						setIsSubmitting(false);
						if (e?.status === 409) {
							onLogout(e?.status);
						}
						toast.error(e?.data?.msg || e.message);
					},
					complete: () => {
						setIsSubmitting(false);
					}
				});
			} catch (error: any) {
				setIsSubmitting(false);
				toast.error(error?.message || 'An error occurred during signup');
				console.error('API call error:', error);
			}
		} catch (error) {
			setIsSubmitting(false);
			console.error('Form validation error:', error);
		}
	};

	return (
		<PageWrapper isBeforeLoginRoute={true} isProtectedRoute={false} className='bg-white' name='Sign Up'>
			<div className='flex h-full items-center justify-center'>
				<div className='w-full h-full flex-col flex md:flex-row'>
					{/* Gradient section - hidden on mobile, visible on desktop */}
					<div ref={gradientRef} className='hidden md:flex md:w-1/2 interactive-gradient'>
						<div className='mx-auto my-5 flex basis-2/3 relative z-10'>
							<img src={therapixDarkLogo} alt='THERapix' className='home-logo' />
						</div>
					</div>
					
					{/* Form section - full width on mobile, half on desktop */}
					<div className='w-full md:w-1/2 text-black flex justify-center items-center px-4 py-6 md:px-0 md:py-0 overflow-y-auto flex-center-mobile min-h-screen'>
						<div className='flex w-full max-w-md flex-col gap-6 md:gap-8 items-center'>
							<div className="flex flex-col items-center md:items-start md:flex-row md:gap-4 w-full">
								<div className='logo-small mb-4 md:mb-0 flex justify-center md:justify-start w-full md:w-auto'>
									<img src={theRapixLOgoSmall} alt='THERapix' className='home-logo' />
								</div>
								<div className='flex flex-col items-center md:items-start gap-2 md:gap-4 w-full'>
									<span className='text-3xl md:text-4xl font-semibold text-center md:text-left text-center-mobile'>Sign up</span>
									<span className='text-center md:text-left text-center-mobile'>Sign up with email address</span>
								</div>
							</div>
							<form className='flex flex-col gap-4 w-full items-center form-field-wrapper' noValidate onSubmit={handleFormSubmit}>
								{/* First name and last name - stack on mobile, flex on larger screens */}
								<div className='flex flex-col md:flex-row gap-4 md:gap-2 w-full'>
									<div className="w-full">
										<Validation
											isValid={formik.isValid}
											isTouched={formik.touched.firstName}
											invalidFeedback={formik.errors.firstName}>
											<FieldWrap
												firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
												<Input
													dimension='default'
													id='firstName'
													autoComplete='firstName'
													name='firstName'
													placeholder='First Name'
													value={formik.values.firstName}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													className="w-full"
												/>
											</FieldWrap>
										</Validation>
									</div>
									<div className="w-full">
										<Validation
											isValid={formik.isValid}
											isTouched={formik.touched.lastName}
											invalidFeedback={formik.errors.lastName}>
											<FieldWrap
												firstSuffix={<Icon icon='HeroUser' className='mx-2' />}>
												<Input
													dimension='default'
													id='lastName'
													autoComplete='lastName'
													name='lastName'
													placeholder='Last Name'
													value={formik.values.lastName}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													className="w-full"
												/>
											</FieldWrap>
										</Validation>
									</div>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.email}
										invalidFeedback={formik.errors.email}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroEnvelope' className='mx-2' />}>
											<Input
												dimension='default'
												id='email'
												autoComplete='email'
												name='email'
												placeholder='Email'
												value={formik.values.email}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
											/>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.mobile}
										invalidFeedback={formik.errors.mobile}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroPhone' className='mx-2' />}>
											<Input
												dimension='default'
												id='mobile'
												autoComplete='mobile'
												name='mobile'
												type='text'
												placeholder='Mobile'
												value={formik.values.mobile}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
											/>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.password}
										invalidFeedback={formik.errors.password}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroKey' className='mx-2' />}
											lastSuffix={
												<Icon
													className='mx-2 cursor-pointer'
													icon={passwordShowStatus ? 'HeroEyeSlash' : 'HeroEye'}
													onClick={() => {
														setPasswordShowStatus(!passwordShowStatus);
													}}
												/>
											}>
											<Input
												dimension='default'
												type={passwordShowStatus ? 'text' : 'password'}
												autoComplete='new-password'
												id='password'
												name='password'
												placeholder='Password'
												value={formik.values.password}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
											/>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.confirmpassword}
										invalidFeedback={formik.errors.confirmpassword}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroKey' className='mx-2' />}
											lastSuffix={
												<Icon
													className='mx-2 cursor-pointer'
													icon={confirmPasswordShowStatus ? 'HeroEyeSlash' : 'HeroEye'}
													onClick={() => {
														setConfirmPasswordShowStatus(!confirmPasswordShowStatus);
													}}
												/>
											}>
											<Input
												dimension='default'
												type={confirmPasswordShowStatus ? 'text' : 'password'}
												autoComplete='current-password'
												id='confirmpassword'
												name='confirmpassword'
												placeholder='Confirm Password'
												value={formik.values.confirmpassword || ''}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												className="w-full"
											/>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.country}
										invalidFeedback={formik.errors.country}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroPencil' className='mx-2' />}>
											<Select
												dimension='default'
												id='country'
												name='country'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.country}
												placeholder='Country'
												className="w-full">
												{countries.map((i) => (
													i ? (<option key={i.label} value={i.value}>
														{i.label}
													</option>) : null
												))}
											</Select>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full'>
									<Validation
										isValid={formik.isValid}
										isTouched={formik.touched.state}
										invalidFeedback={formik.errors.state}>
										<FieldWrap
											firstSuffix={<Icon icon='HeroPencil' className='mx-2' />}>
											<Select
												dimension='default'
												id='state'
												name='state'
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.state}
												placeholder='State'
												disabled={!formik.values.country}
												className="w-full">
												{statesOptions.map((i) => (
													i ? (<option key={i.label} value={i.value}>
														{i.label}
													</option>) : null
												))}
											</Select>
										</FieldWrap>
									</Validation>
								</div>
								<div className='w-full mb-4'>
									<label className='flex items-center gap-2 text-sm'>
										<input
											type="checkbox"
											checked={isChecked}
											className="h-4 w-4 flex-shrink-0"
											onChange={handleCheckboxChange}
										/>
										<div className='flex flex-wrap items-center'>
											<span>I agree to the</span>
											<Link
												to="https://www.therapix.ai/terms-of-service"
												className="secondary-text hover:text-inherit mx-1"
												target="_blank"
												rel="noopener noreferrer"
											>
												Terms of Use
											</Link>
											<span>and</span>
											<Link
												to="https://www.therapix.ai/privacy-policy"
												className="secondary-text hover:text-inherit mx-1"
												target="_blank"
												rel="noopener noreferrer"
											>
												Privacy Policy
											</Link>
										</div>
									</label>
								</div>
								<div className='w-full flex-center-mobile'>
									<Button
										isLoading={isSubmitting}
										size='lg'
										variant='solid'
										className='primary-bg w-full font-semibold'
										isDisable={!isChecked}
										onClick={(e) => {
											e.preventDefault();
											handleFormSubmit(e);
										}}>
										Sign up
									</Button>
								</div>
							</form>
							<div className='w-full text-center flex-center-mobile'>
								<span className='flex justify-center items-center gap-2 text-sm flex-wrap'>
									<span className='text-zinc-400 dark:text-zinc-600 text-center-mobile'>
										Already have an account?
									</span>
									<Link to={`${authPages.loginPage.to}`} className='hover:text-inherit font-semibold text-md'>
										Sign in
									</Link>
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</PageWrapper>
	);
};

export default SignupPage;